import styles from "./ClientNotes.module.scss";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Spin } from "antd";
import MainTitle from "../../../../components/ui/MainTitle/MainTitle";
import CustomButton from "../../../../components/ui/CustomButton/CustomButton";
import ConfirmPopup from "../../../../components/ConfirmPopup/ConfirmPopup";
import NotePopup from "./NotePopup/NotePopup";
import plusIconWhite from "../../../../assets/images/icons/plus-white.svg";
import dots from "../../../../assets/images/icons/dots.svg";
import editIcon from "../../../../assets/images/icons/edit-dark.svg";
import deleteIcon from "../../../../assets/images/icons/delete-red.svg";
import { useGetUserNotesQuery, useUserNotesCache } from "../../../../api/queries";
import { useUserNoteMutations } from "../../../../api/mutations";
import useAppDispatch from "../../../../hooks/useAppDispatch";
import { openSuccessMessage } from "../../../../store/message/messageSlice";
import { handleError } from "../../../../helper/errorHandler";
import { IUserNote, IUserNoteModel } from "../../../../models/UserNote";

// undefined - closed, null - adding, a note - editing it
type EditorState = IUserNote | null | undefined;

const ClientNotes = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { id = "" } = useParams();
  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } =
    useGetUserNotesQuery(id);
  const { addNote, replaceNote, removeNote } = useUserNotesCache(id);
  const { createNote, updateNote, deleteNote, isSaving } = useUserNoteMutations(id);
  const [editing, setEditing] = useState<EditorState>(undefined);
  const [deleting, setDeleting] = useState<IUserNote | null>(null);

  const notes = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);
  // From the last loaded page - the freshest count
  const total = data?.pages[data.pages.length - 1].total ?? 0;
  const remaining = Math.max(total - notes.length, 0);

  const onError = (e: unknown) => handleError(e, dispatch, t);

  const onSave = (model: IUserNoteModel) => {
    const onSuccess = (message: string, apply: (note: IUserNote) => void) => (note: IUserNote) => {
      apply(note);
      dispatch(openSuccessMessage(t(message)));
      setEditing(undefined);
    };

    if (editing) {
      updateNote(
        { id: editing.id, model },
        { onSuccess: onSuccess("NoteUpdatedSuccessfully", replaceNote), onError }
      );
    } else {
      createNote(model, { onSuccess: onSuccess("NoteCreatedSuccessfully", addNote), onError });
    }
  };

  const onDelete = () => {
    if (!deleting) return;
    const { id: noteId } = deleting;
    deleteNote(noteId, {
      onSuccess: () => {
        removeNote(noteId);
        dispatch(openSuccessMessage(t("NoteDeletedSuccessfully")));
      },
      onError,
    });
    setDeleting(null);
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <MainTitle text={t("Notes")} />
        <div className={styles.addBtn}>
          <CustomButton
            text={t("Add")}
            type={"button"}
            typeStyle={"fill"}
            icon={plusIconWhite}
            altIcon={"add"}
            functionOnClick={() => setEditing(null)}
          />
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loader}>
          <Spin />
        </div>
      ) : notes.length === 0 ? (
        <p className={styles.empty}>{t("NoNotesYet")}</p>
      ) : (
        <ul className={styles.list}>
          {notes.map((note) => (
            <li key={note.id} className={styles.card}>
              <div className={styles.meta}>
                <span className={styles.author}>
                  {note.author
                    ? `${note.author.firstname} ${note.author.lastname}`
                    : t("DeletedUser")}
                </span>
                <span className={styles.separator}>·</span>
                <span>{dayjs(note.createdAt).format("MMM D, YYYY · h:mm A")}</span>
                {note.editedAt && (
                  <span
                    className={styles.edited}
                    title={dayjs(note.editedAt).format("MMM D, YYYY · h:mm A")}
                  >
                    ({t("Edited").toLowerCase()})
                  </span>
                )}
              </div>
              <h3 className={styles.noteTitle}>{note.title}</h3>
              <p className={styles.description}>{note.description}</p>

              {note.canManage && (
                <div className={styles.actions}>
                  <img src={dots} alt={"actions"} />
                  <ul className={styles.dropdown}>
                    <li className={styles.dropdownItem} onClick={() => setEditing(note)}>
                      <img src={editIcon} alt={"edit"} />
                      {t("Edit")}
                    </li>
                    <li
                      className={`${styles.dropdownItem} ${styles.danger}`}
                      onClick={() => setDeleting(note)}
                    >
                      <img src={deleteIcon} alt={"delete"} />
                      {t("Delete")}
                    </li>
                  </ul>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {hasNextPage && (
        <div className={styles.showMore}>
          <CustomButton
            text={isFetchingNextPage ? "…" : `${t("ShowMore")} (${remaining})`}
            type={"button"}
            typeStyle={"outline"}
            functionOnClick={() => {
              if (!isFetchingNextPage) fetchNextPage();
            }}
          />
        </div>
      )}

      {editing !== undefined && (
        <NotePopup
          note={editing}
          isSaving={isSaving}
          onSave={onSave}
          closePopup={() => setEditing(undefined)}
        />
      )}

      {deleting && (
        <ConfirmPopup
          title={t("AreYouSureYouWantToDeleteThisNote")}
          onClickYes={onDelete}
          onClickNo={() => setDeleting(null)}
        />
      )}
    </div>
  );
};

export default ClientNotes;
